// @flow
import React, { Suspense } from "react";
import Card from "@material-ui/core/Card";
import CardActionArea from "@material-ui/core/CardActionArea";
import CardContent from "@material-ui/core/CardContent";
import Box from "@material-ui/core/Box";
import { createStyles, makeStyles, Theme } from "@material-ui/core/styles";
import {
  useFirestore,
  useFirestoreDocData,
  useFirestoreDocDataOnce,
} from "reactfire";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import formatDates from "../../common/formatDates";
import moment from "moment";
import type { UserData } from "../../common/types";
import PersonRoundedIcon from "@material-ui/icons/PersonRounded";
import ProfileTicketCompanions from "./ProfileTicketCompanions";
import Skeleton from "@material-ui/lab/Skeleton";
import { useHistory } from "react-router-dom";

type Props = {
  gameId: string,
  userId: string,
  userData: UserData,
};

function ProfileTicket({ gameId, userId, userData, disabled }: Props) {
  const DOT_SIZE = 16;
  const backgroundKey = disabled ? "paper" : "default";
  const useStyles = makeStyles((theme: Theme) =>
    createStyles({
      wrapper: {
        background: "#5D5CDE",
        boxShadow: "0 0 0 0",
        borderTopLeftRadius: 9,
        borderTopRightRadius: 9,
        borderBottomRightRadius: 0,
        borderBottomLeftRadius: 0,
        color: "white",
        borderBottomWidth: 2,
        borderBottomStyle: "dashed",
        borderBottomColor: theme.palette.background[backgroundKey],
        position: "relative",
        overflow: "inherit",
      },
      leftDot: {
        display: "block",
        backgroundColor: theme.palette.background[backgroundKey],
        position: "absolute",
        width: DOT_SIZE,
        height: DOT_SIZE,
        bottom: -(DOT_SIZE / 2),
        left: -(DOT_SIZE / 2),
        borderRadius: DOT_SIZE,
      },
      rightDot: {
        display: "block",
        backgroundColor: theme.palette.background[backgroundKey],
        position: "absolute",
        width: DOT_SIZE,
        height: DOT_SIZE,
        bottom: -(DOT_SIZE / 2),
        right: -(DOT_SIZE / 2),
        borderRadius: DOT_SIZE,
      },
      attachment: {
        background: "#5D5CDE",
        boxShadow: "0 0 0 0",
        borderBottomLeftRadius: 9,
        borderBottomRightRadius: 9,
        borderTopRightRadius: 0,
        borderTopLeftRadius: 0,
        color: "white",
      },
      textSecondary: {
        color: "#CECEF5",
      },
      ticketNumber: {
        color: "white",
        backgroundColor: "rgba(255,255,255,0.1)",
        fontWeight: "bold",
        textTransform: "uppercase",
        borderRadius: 3,
        padding: "3px 7px",
        "& > small": {
          color: "#CECEF5",
          fontWeight: "normal",
        },
      },
      personList: {
        "& > *": {
          marginBottom: theme.spacing(1),
        },
        "& > :last-child": {
          marginBottom: 0,
        },
      },
      ticketId: {
        fontSize: 14,
        lineHeight: "24px",
        minWidth: 48,
        padding: "0 8px",
        borderRadius: 40,
        backgroundColor: "#bed0f5",
        display: "block",
      },
    })
  );
  const classes = useStyles();
  const firestore = useFirestore();
  const history = useHistory();

  const gameRef = firestore.collection("games").doc(gameId);
  const game = useFirestoreDocDataOnce(gameRef);

  const ticketReservationRef = gameRef
    .collection("ticketReservations")
    .doc(userId);
  const ticketReservation = useFirestoreDocData(ticketReservationRef);

  return (
    <Box mb={2}>
      <CardActionArea
        onClick={
          !disabled ? () => history.push(`/ticket/${gameId}`) : undefined
        }
        disabled={disabled}
      >
        <Card
          classes={{
            root: classes.wrapper,
          }}
        >
          <CardContent>
            <Grid
              container
              justify="space-between"
              wrap="nowrap"
              alignItems="center"
            >
              <Grid item>
                <Typography variant="h6" component="p">
                  <b>{formatDates.dayAndDate(game.date)}</b>
                </Typography>
              </Grid>
              <Grid item>
                <Typography align="right" variant="h6" component="p">
                  {formatDates.time(game.date)} Uhr
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              justify="space-between"
              wrap="nowrap"
              alignItems="center"
            >
              <Grid item>
                <Typography variant="body2" className={classes.ticketNumber}>
                  <small>NR</small> {ticketReservation.id}
                </Typography>
              </Grid>
              <Grid item>
                <Typography
                  align="right"
                  variant="body2"
                  className={classes.textSecondary}
                >
                  Einlass ab{" "}
                  {moment(game.date).subtract(60, "minutes").format("HH:mm")}{" "}
                  Uhr
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
          <div className={classes.leftDot} />
          <div className={classes.rightDot} />
        </Card>
        <Card
          classes={{
            root: classes.attachment,
          }}
        >
          <CardContent className={classes.personList}>
            <ProfileTicketPersonRow name={userData.name} />
            {ticketReservation.companionIds != null &&
              ticketReservation.companionIds.length > 0 && (
                <Suspense
                  fallback={
                    <>
                      {ticketReservation.companionIds.map((cid) => (
                        <ProfileTicketPersonRow
                          key={cid}
                          name={<Skeleton width={120} />}
                        />
                      ))}
                    </>
                  }
                >
                  <ProfileTicketCompanions
                    userId={userId}
                    ticketReservation={ticketReservation}
                  />
                </Suspense>
              )}
          </CardContent>
        </Card>
      </CardActionArea>
    </Box>
  );
}

export function ProfileTicketPersonRow({ name, paid }) {
  return (
    <Grid container alignItems="center" wrap="nowrap">
      <Grid item style={{ lineHeight: 0, marginRight: 10 }}>
        <PersonRoundedIcon />
      </Grid>
      <Grid item container>
        <Typography variant="body2">{name}</Typography>
      </Grid>
      {!paid && (
        <Grid item>
          <Typography variant="body2">3,00&nbsp;€</Typography>
        </Grid>
      )}
    </Grid>
  );
}

export default ProfileTicket;
